export type ISection = {
  title: string;
  forms: IForm[];
};

export type TFormType =
  | 'CHECKBOX'
  | 'COMPLEX'
  | 'DATE'
  | 'MULTIPLE_CHOICE'
  | 'SELECT'
  | 'TEXTAREA'
  | 'TIME'
  | 'TEXT'
  | 'TITLE';

export type IForm<TChoices extends string[] = string[]> = {
  type: TFormType;
  title: string;
  id: string;
  noteOption?: {
    type?: TFormType;
    label: string;
    id: string;
  };
  isRequired?: boolean;
  hasOtherOption?: boolean;
  delimeter?: string;
  otherOptionLabel?: string;
  choices?: string[];
  requiredIf?: {
    key: IForm<TChoices>['id'];
    value: TChoices[number];
  };
  notRequiredIf?: {
    key: IForm<TChoices>['id'];
    value: TChoices[number];
  };
  subForms?: IForm[];
  placeholder?: string;
};

export const formSections: ISection[] = [
  {
    title: 'Initial Overview CKD',
    forms: [
      {
        type: 'MULTIPLE_CHOICE',
        title: 'How long have you been treated for CKD?',
        id: 'howLongTreatedCkd',
        choices: [
          '< 6 months',
          '1 year',
          '1-2 years',
          '3-5 years',
          '> 5 years',
          'Lifetime',
        ],
        noteOption: {
          type: 'TEXTAREA',
          label: 'How does CKD affect your life?',
          id: 'howLongTreatedCkdNote',
        },
      },
      {
        type: 'MULTIPLE_CHOICE',
        title:
          "How is Patient's understanding of the effects of CKD on the body?",
        id: 'effectsOnBody',
        choices: ['Good', 'Fair', 'Poor'],
        noteOption: {
          type: 'TEXTAREA',
          label: 'Which Provider do you work with to manage CKD? (Limit 4)',
          id: 'effectsOnBodyNote',
        },
      },
      {
        type: 'MULTIPLE_CHOICE',
        title: 'Have you been hospitalized for CKD?',
        id: 'hospitalized',
        hasOtherOption: false,
        choices: ['Yes', 'No'],
      },
      {
        type: 'MULTIPLE_CHOICE',
        title: 'Have you received outpatient services for CKD?',
        id: 'outpatientServices',
        hasOtherOption: false,
        choices: ['Yes', 'No'],
      },
      {
        type: 'MULTIPLE_CHOICE',
        title: 'Do you have a family history of CKD?',
        id: 'familyHistory',
        hasOtherOption: false,
        choices: ['Yes', 'No'],
      },
      {
        type: 'MULTIPLE_CHOICE',
        title: 'Have you had a pretransplant assessment?',
        id: 'pretransplantAssessment',
        hasOtherOption: false,
        choices: ['Yes', 'No'],
        noteOption: {
          type: 'TEXTAREA',
          label: 'Nurse Note:',
          id: 'pretransplantAssessmentNote',
        },
      },
    ],
  },
  {
    title: 'Signs & Symptoms',
    forms: [
      {
        type: 'CHECKBOX',
        title: 'Patient recognizes these Signs and Symptoms of CKD (Limit 4)',
        id: 'recognizesSignsSymptoms',
        choices: [
          'Swelling in hands, feet, or legs',
          'Discomfort or swelling to the abdomen',
          'Frequent urinary urgency',
          'Fatigue, lack of energy',
          'Loss of appetite',
          'Nausea, vomiting, taste changes',
          'Oral ulcerations',
        ],
      },
      {
        type: 'CHECKBOX',
        title:
          'SN instructed Patient on these Signs and Symptoms of CKD (Limit 4)',
        id: 'snInstructions',
        choices: [
          'Swelling in hands, feet, or legs',
          'Discomfort or swelling to the abdomen',
          'Frequent urinary urgency',
          'Fatigue, lack of energy',
          'Loss of appetite',
          'Nausea, vomiting, taste changes',
          'Oral ulcerations',
        ],
      },
      {
        type: 'CHECKBOX',
        title:
          'SN instructed Patient to notify Provider if any of these Signs and Symptoms occur',
        id: 'notifyProvider',
        choices: ['Yes'],
      },
      {
        type: 'CHECKBOX',
        title: 'SN utilized teach-back method with Patient',
        id: 'symptomsTeachBack',
        choices: ['Yes'],
      },
      {
        type: 'MULTIPLE_CHOICE',
        title: 'Was Patient able to verbalize understanding of the education?',
        id: 'symptomsVerbalizeEducation',
        choices: ['Able', 'Partially able', 'Unable'],
        noteOption: {
          type: 'TEXTAREA',
          label: 'Nurse Note:',
          id: 'symptomsVerbalizeEducationNote',
        },
      },
    ],
  },
  {
    title: 'Limiting Fluids and Monitors Weight',
    forms: [
      {
        type: 'CHECKBOX',
        title:
          'SN educated Patient on the importance of limiting fluids (Limit 4)',
        id: 'limitingFluids',
        choices: [
          'You need water to live, but when you have kidney disease, you may not need as much. This is because damaged kidneys do not get rid of extra fluid as well as they should',
          'Too much fluid in your body can be dangerous',
          'It can cause high blood pressure, swelling and heart failure',
          'Extra fluid can also build up around your lungs and make it hard to breathe',
          'Ask your Provider or dietitian if you need to limit fluids',
          "if you do need to limit fluids, measure your fluids and drink from small cups to help you keep track of how much you've had to drink",
          'Limit sodium to help cut down on thirst. At times, you may still feel thirsty',
        ],
      },
      {
        type: 'CHECKBOX',
        title: 'To help quench your thirst, you might try to (Limit 4)',
        id: 'helpQuenchThirst',
        choices: [
          'Avoid salty foods',
          'Stay cool on hot days',
          'Chew gum',
          'Rinse your mouth',
          'Suck on a piece of ice, mints or hard candy',
        ],
      },
      {
        type: 'CHECKBOX',
        title: 'SN utilized teach-back method with Patient',
        id: 'fluidsTeachBack',
        choices: ['Yes'],
      },
      {
        type: 'MULTIPLE_CHOICE',
        title: 'Was Patient able to verbalize understanding of the education?',
        id: 'fluidsVerbalizeEducation',
        choices: ['Able', 'Partially able', 'Unable'],
        noteOption: {
          type: 'TEXTAREA',
          label: 'Nurse Note:',
          id: 'fluidsVerbalizeEducationNote',
        },
      },
      {
        type: 'MULTIPLE_CHOICE',
        title: 'Do you monitor weight?',
        id: 'monitorWeight',
        choices: ['No', 'Yes, every day', 'Yes, but not every day'],
      },
      {
        type: 'CHECKBOX',
        title: 'SN instructed Patient on why monitoring weight is important',
        id: 'monitorWeightImportant',
        choices: [
          'A sudden weight gain can mean more fluid is building up in your body and your heart failure is getting worse',
          'Watch for weight gain of 2-3 pounds over 1-2 days or 5 pounds over a week',
          'You can gain up to 10 Ibs of "extra" weight before feeling bad or swelling',
          'Call your provider if you have a sudden weight gain',
        ],
      },
      {
        type: 'MULTIPLE_CHOICE',
        title: 'Do you have a scale?',
        id: 'haveScale',
        choices: [
          'Yes, Patient has a scale and uses it',
          'Yes, Patient has a scale but is not using it',
          'No',
        ],
      },
      {
        type: 'CHECKBOX',
        title: 'SN instructed Patient on consistent steps to measure weight',
        id: 'measureWeight',
        choices: [
          'Weigh at the same time in the morning, after you urinate',
          'Weigh while wearing what you sleep in, before eating or drinking',
          'Weigh using the same scale on a flat, hard surface',
          'After weighing, write your weight down on the weight log',
        ],
      },
      {
        type: 'COMPLEX',
        title: "What are Patient's recent WEIGHTS?",
        id: 'recentWeights',
        subForms: [
          {
            id: 'vitalType',
            title: 'Select Vital Type',
            type: 'SELECT',
            choices: ['Weight'],
          },
          {
            id: 'vitalDate',
            title: 'Date',
            type: 'DATE',
          },
          {
            id: 'vitalTime',
            title: 'Time',
            type: 'TIME',
          },
          {
            id: 'vitalValue',
            title: 'Pounds',
            type: 'TEXT',
            placeholder: 'ex. 150',
          },
        ],
      },
      {
        type: 'CHECKBOX',
        title: 'Daily Weight Monitoring',
        id: 'dailyWeightMonitoring',
        choices: [
          'A sudden weight gain can mean more fluid is building up in your body and your heart failure is getting worse',
          'Watch for weight gain of 2-3 pounds over 1-2 days or 5 pounds over a week',
          'You can gain up to 10 lbs of "extra" weight before feeling bad or swelling',
          'Call your provider if you have a sudden weight gain',
          'Weigh at the same time in the morning, after you urinate',
          'Weigh while wearing what you sleep in, before eating or drinking',
          'Weigh using the same scale on a flat, hard surface',
          'After weighing, write your weight down on the weight log',
        ],
      },
      {
        title: 'Diets-CKD',
        id: '_diets-ckd',
        type: 'TITLE',
      },
      {
        type: 'CHECKBOX',
        title:
          'SN educated Patient on tips for limiting phosphorus in the diet for CKD',
        id: 'limitPhosphorusInDiet',
        choices: [
          'Dairy foods are the major source of phosphorus in the diet',
          'Limit milk to 1 cup per day',
          'If you use yogurt or cheese instead of liquid milk, have only one container of yogurt or 1.5 ounces of cheese per day',
          'White or Italian bread and low-salt crackers made with white flour have less phosphorus than whole-grain bread and crackers',
          'Soft drinks contain phosphorus, so only drink clear ones',
          'Beer also has phosphorus. Avoid all kinds',
        ],
      },
      {
        type: 'CHECKBOX',
        title: 'SN utilized teach-back method with Patient',
        id: 'weightTeachBack',
        choices: ['Yes'],
      },
      {
        type: 'MULTIPLE_CHOICE',
        title: 'Was Patient able to verbalize understanding of the education?',
        id: 'weightVerbalizeEducation',
        choices: ['Able', 'Partially able', 'Unable'],
        noteOption: {
          type: 'TEXTAREA',
          label: 'Nurse Note:',
          id: 'weightVerbalizeEducationNote',
        },
      },
      {
        type: 'TEXTAREA',
        title: 'Explain need for reinforcement',
        id: 'reinforcementNote',
      },
    ],
  },
];
